import { useState, useEffect, useMemo } from "react";
import useBranchStore from "@/store/branchStore";
import useAuthStore from "@/store/authStore";
import { Building2, Lock } from "lucide-react";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BranchSelector = () => {
  const { selectedBranch, setSelectedBranch } = useBranchStore();
  const { hasRole, token } = useAuthStore();
  // `allBranches` — every active branch in the org (so the user always sees
  //   the full list with locks on the ones they can't access).
  // `allowedBranches` — branches the current user can actually target.
  // `isAllAccess` — short-circuits the lock UI for Master Admin et al.
  const [allBranches, setAllBranches] = useState([]);
  const [allowedBranches, setAllowedBranches] = useState([]);
  const [isAllAccess, setIsAllAccess] = useState(false);

  // Hide branch selector for Demand Planner - they see all branches
  const isDemandPlanner = hasRole("DEMAND_PLANNER") && !hasRole("MASTER_ADMIN");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [allRes, mineRes] = await Promise.all([
          axios.get(`${API}/branches/names`, { headers }),
          axios.get(`${API}/branch-ops/my-branches`, { headers }),
        ]);
        if (cancelled) return;
        const all = allRes.data?.branches || [];
        const mine = mineRes.data?.branches || [];
        const allAccess = Boolean(mineRes.data?.is_all_access);
        setAllBranches(all);
        setAllowedBranches(mine);
        setIsAllAccess(allAccess);
        // Defensive: if the persisted branch isn't accessible to this user
        // (e.g. they logged out as Master Admin and back in as Branch Ops),
        // swap it for the first allowed one so every page query is safe.
        const ok = allAccess || mine.includes(selectedBranch);
        if (!ok) {
          setSelectedBranch(mine[0] || "");
        }
      } catch (error) {
        console.error("Failed to fetch branches:", error);
        setAllBranches([]);
        setAllowedBranches([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const allowedSet = useMemo(() => new Set(allowedBranches), [allowedBranches]);

  if (isDemandPlanner) return null;

  return (
    <div className="p-4 border-b border-zinc-800" data-testid="branch-selector">
      <div className="flex items-center gap-3">
        <Building2 className="w-5 h-5 text-primary shrink-0" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <Select
            value={selectedBranch || undefined}
            onValueChange={(v) => {
              // Hard guard: even if Radix surfaces a disabled item via
              // keyboard nav, we never accept an out-of-scope branch.
              if (!isAllAccess && !allowedSet.has(v)) return;
              setSelectedBranch(v);
            }}
          >
            <SelectTrigger
              className="bg-zinc-800 text-white border border-zinc-700 rounded-sm h-9 text-sm font-mono focus:ring-primary"
              data-testid="branch-select"
            >
              <SelectValue placeholder="Select branch" />
            </SelectTrigger>
            <SelectContent>
              {allBranches.map((name) => {
                const allowed = isAllAccess || allowedSet.has(name);
                return (
                  <SelectItem
                    key={name}
                    value={name}
                    disabled={!allowed}
                    data-testid={`branch-select-option-${name.replace(/\s+/g, "-")}`}
                    className={!allowed ? "text-zinc-400 bg-zinc-50/60" : ""}
                  >
                    <span className="flex items-center gap-2">
                      {!allowed ? (
                        <Lock className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                      ) : (
                        <span className="w-3.5 h-3.5 shrink-0" />
                      )}
                      <span>{name}</span>
                      {!allowed && (
                        <span className="text-[10px] text-zinc-400 ml-1">(no access)</span>
                      )}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-2 text-xs text-zinc-500 font-mono">
        Current Branch
        {!isAllAccess && allowedBranches.length === 1 && (
          <span className="ml-2 text-amber-400/80">· locked</span>
        )}
      </div>
    </div>
  );
};

export default BranchSelector;
